import React, { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import NavBarListItem from 'components/NavKit/NavBarListItem'
import NavBarListItemDivider from 'components/NavKit/NavBarListItemDivider'
import BodyText from 'LuxKit/Typography/BodyText'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import useMerchandiseCategories from 'hooks/Merchandise/useMerchandiseCategories'
import { getMerchandiseCategorySearchUrl } from '../merchandiseUtils'
import MerchandiseMegaMenuFeaturedBrands from './components/MerchandiseMegaMenuFeaturedBrands'
import IconPlaceholder from './components/IconPlaceholder'

const CategoriesSection = styled.div`
  display: flex;
  flex-direction: column;
  padding-inline: ${rem(8)};
`

const Spacer = styled.div`
  height: ${rem(24)};
`

const BrandsSection = styled.div`
  padding-inline: ${rem(20)};
`

const StyledModalHeader = styled(ModalHeader)`
  padding: ${rem(16)} ${rem(20)};
`

const StyledNavBarListItem = styled(NavBarListItem)`
  padding-block: ${rem(8)};
  padding-inline: ${rem(0)};
  gap: ${rem(8)};
`

const StyledDivider = styled(NavBarListItemDivider)`
  height: ${rem(2)};
`

interface Props {
  category: App.MerchandiseCategory
  onCloseAll?: () => void
}

function MerchandiseSubcategoryDrawer(props: Props) {
  const { category, onCloseAll } = props
  const modalElement = useModalElementContext()

  const [subcategories] = useMerchandiseCategories({
    categoryIds: category.childCategoryIds,
  }, { disabled: !category })

  const handleSubcategoryClick = useCallback(() => {
    modalElement.resolve()
    onCloseAll?.()
  }, [modalElement, onCloseAll])

  return (
    <ModalBase mode="drawer">
      <StyledModalHeader title={category.name} />
      <ModalBody>
        <ModalContent flush>
          <CategoriesSection>
            {/* "All {category}" option */}
            <StyledNavBarListItem
              title={<BodyText variant="medium" weight="bold">All {category.name}</BodyText>}
              to={getMerchandiseCategorySearchUrl(category)}
              onClick={handleSubcategoryClick}
              endIcon={<IconPlaceholder size={24} />}
            />

            {/* Subcategories */}
            {subcategories.map((subcategory) => (
              <StyledNavBarListItem
                key={subcategory.id}
                title={subcategory.name}
                to={getMerchandiseCategorySearchUrl(subcategory)}
                onClick={handleSubcategoryClick}
                endIcon={<IconPlaceholder size={24} />}
              />
            ))}
          </CategoriesSection>

          {/* Spacer + Divider + Spacer */}
          <Spacer />
          <StyledDivider />
          <Spacer />

          {/* Featured Brands */}
          <BrandsSection>
            <MerchandiseMegaMenuFeaturedBrands
              categoryId={category.id}
              onClose={handleSubcategoryClick}
              columns={3}
              limit={12}
              minBrands={4}
              layout="mobile"
            />
          </BrandsSection>
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default MerchandiseSubcategoryDrawer
